// Custom React Query hooks for Connector data fetching
// This replaces useEffect patterns with optimized caching and performance

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ConnectorManager } from '../ConnectorManager'
import { Broker } from '../types'
import {
  getSecureItem,
  setSecureItem,
  removeSecureItem
} from '../utils/secureStorage'

// Query keys for consistent caching
export const QUERY_KEYS = {
  brokers: ['brokers'] as const,
  accountInfo: (brokerId: string) => ['accountInfo', brokerId] as const,
  balance: (brokerId: string) => ['balance', brokerId] as const,
  positions: (brokerId: string) => ['positions', brokerId] as const,
  brokerStatus: (brokerId: string) => ['brokerStatus', brokerId] as const,
} as const

// Singleton connector manager instance
const connectorManager = ConnectorManager.getInstance()

// Hook to get all brokers with their connection status
export const useBrokers = () => {
  return useQuery({
    queryKey: QUERY_KEYS.brokers,
    queryFn: async (): Promise<Broker[]> => {
      // Get brokers from manager
      const brokers = connectorManager.getBrokers()
      
      // Check stored enabled broker
      const storedEnabledBroker = getSecureItem('broker_enabled') || 'ninx'
      
      // Map brokers with enabled status
      return brokers.map(broker => ({
        ...broker,
        enabled: broker.id === storedEnabledBroker
      }))
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - brokers list doesn't change often
    gcTime: 10 * 60 * 1000, // 10 minutes in cache
  })
}

// Hook to get account information for a specific broker
export const useAccountInfo = (brokerId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.accountInfo(brokerId),
    queryFn: async () => {
      const connector = connectorManager.getConnector(brokerId)
      if (!connector) {
        throw new Error(`Connector not found for broker: ${brokerId}`)
      }
      return await connector.getAccountInfo()
    },
    enabled: enabled && !!brokerId, // Only run if broker is enabled and connected
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Retry failed requests twice
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  })
}

// Hook to get balance for a specific broker
export const useBalance = (brokerId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.balance(brokerId),
    queryFn: async (): Promise<number> => {
      const connector = connectorManager.getConnector(brokerId)
      if (!connector) {
        throw new Error(`Connector not found for broker: ${brokerId}`)
      }
      return await connector.getBalance()
    },
    enabled: enabled && !!brokerId,
    staleTime: 30 * 1000, // 30 seconds - balance updates frequently
    gcTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

// Hook to get positions for a specific broker
export const usePositions = (brokerId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: QUERY_KEYS.positions(brokerId),
    queryFn: async (): Promise<any[]> => {
      const connector = connectorManager.getConnector(brokerId)
      if (!connector) {
        throw new Error(`Connector not found for broker: ${brokerId}`)
      }
      return await connector.getPositions()
    },
    enabled: enabled && !!brokerId,
    staleTime: 30 * 1000, // 30 seconds - positions update frequently
    gcTime: 2 * 60 * 1000, // 2 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  })
}

// Hook to check if a broker is connected
export const useBrokerConnection = (brokerId: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.brokerStatus(brokerId),
    queryFn: async (): Promise<boolean> => {
      const connector = connectorManager.getConnector(brokerId)
      return connector?.isConnectedToBroker?.() || false
    },
    enabled: !!brokerId,
    staleTime: 10 * 1000, // 10 seconds
    gcTime: 30 * 1000, // 30 seconds
    refetchInterval: 30 * 1000, // Check connection every 30 seconds
  })
}

// Mutation to connect to a broker
export const useConnectBroker = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({ brokerId, credentials }: { brokerId: string; credentials: { apiKey: string; apiSecret: string } }) => {
      const success = await connectorManager.connectBroker(brokerId, credentials)
      if (!success) {
        throw new Error(`Failed to connect to broker: ${brokerId}`)
      }
      return success
    },
    onSuccess: (_, { brokerId, credentials }) => {
      // Store credentials in session storage on successful connection
      setSecureItem(`${brokerId}_api_key`, credentials.apiKey)
      if (brokerId !== 'groww') {
        setSecureItem(`${brokerId}_api_secret`, credentials.apiSecret)
      }
      
      // Invalidate and refetch related queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brokers })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brokerStatus(brokerId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.accountInfo(brokerId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.balance(brokerId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.positions(brokerId) })
    },
    onError: (error) => {
      console.error('Failed to connect broker:', error)
    }
  })
}

// Mutation to disconnect from a broker
export const useDisconnectBroker = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (brokerId: string) => {
      const connector = connectorManager.getConnector(brokerId)
      if (connector) {
        await connector.disconnect()
      }
      return true
    },
    onSuccess: (_, brokerId) => {
      // Remove stored credentials
      removeSecureItem(`${brokerId}_api_key`)
      removeSecureItem(`${brokerId}_api_secret`)
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brokers })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brokerStatus(brokerId) })
      
      // Remove cached data for this broker
      queryClient.removeQueries({ queryKey: QUERY_KEYS.accountInfo(brokerId) })
      queryClient.removeQueries({ queryKey: QUERY_KEYS.balance(brokerId) })
      queryClient.removeQueries({ queryKey: QUERY_KEYS.positions(brokerId) })
    },
    onError: (error) => {
      console.error('Failed to disconnect broker:', error)
    }
  })
}

// Mutation to toggle broker enabled status
export const useToggleBrokerEnabled = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (brokerId: string) => {
      // Update session storage
      const currentEnabled = getSecureItem('broker_enabled')
      if (currentEnabled === brokerId) {
        removeSecureItem('broker_enabled')
      } else {
        setSecureItem('broker_enabled', brokerId)
        connectorManager.updateBrokerEnabled(brokerId, true)
      }
      return brokerId
    },
    onSuccess: () => {
      // Invalidate brokers query to trigger re-render with new enabled state
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brokers })
    }
  })
}

// Hook to auto-connect stored credentials on app start
export const useAutoConnect = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async () => {
      const brokers = connectorManager.getBrokers()
      const storedEnabledBroker = getSecureItem('broker_enabled') || 'nintrade'
      
      // Auto-connect to enabled broker if credentials are stored
      for (const broker of brokers) {
        if (broker.id === storedEnabledBroker && !broker.isConnected) {
          const storedApiKey = getSecureItem(`${broker.id}_api_key`)
          
          if (broker.id === 'groww' && storedApiKey) {
            // Groww only needs API key
            await connectorManager.connectBroker(broker.id, {
              apiKey: storedApiKey,
              apiSecret: ''
            })
          } else {
            // Other brokers need both API key and secret
            const storedApiSecret = getSecureItem(`${broker.id}_api_secret`)
            if (storedApiKey && storedApiSecret) {
              await connectorManager.connectBroker(broker.id, {
                apiKey: storedApiKey,
                apiSecret: storedApiSecret
              })
            }
          }
        }
      }
      
      return true
    },
    onSuccess: () => {
      // Refresh all broker-related queries
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.brokers })
    }
  })
} 